<template>
  <v-container>
    <!-- Order Header Info -->
    <v-row>
      <!-- Delivery Info -->
      <v-col
        cols="12"
        md="4"
      >
        <div class="d-flex flex-column px-4 text-center">
          <div class="font-weight-bold">
            {{ deliveryHeading }}
          </div>
          <template v-if="step === 1 || step === 2">
            <div class="font-weight-bold">
              {{ deliveryWindow.EarlyTime }} - {{ deliveryWindow.LateTime }}
            </div>
            <div>{{ deliveryWindow.DeliveryDateString }}</div>
          </template>
          <div
            v-else-if="step === 0"
            class="text-center"
          >
            Your delivery has been scheduled but has not left the pharmacy at this time.
          </div>
        </div>
      </v-col>

      <!-- USPack Order Number -->
      <v-col
        cols="12"
        md="4"
        class="text-center d-flex align-center justify-center"
      >
        <h3 class="px-4">
          USPack Order Number: {{ usPackOrderNumber }}
        </h3>
      </v-col>

      <!-- Address Info -->
      <v-col
        cols="12"
        md="4"
        class="text-center"
      >
        <div class="font-weight-bold">
          {{ shipToAddressInfo.Address }}
        </div>
        <div class="font-weight-bold">
          {{ shipToAddressInfo.City }}, {{ shipToAddressInfo.State }} {{ shipToAddressInfo.Zip }}
        </div>
      </v-col>
    </v-row>

    <!-- Tracking + Driver Badge Row -->
    <v-row
      class="my-4"
      align="center"
    >
      <!-- Order Lifecycle -->
      <v-col
        cols="12"
        md="8"
      >
        <order-lifecycle
          :timezone="timezone"
          :step="step"
          :lifecycle-info="lifecycleInfo"
        />
      </v-col>

      <!-- Driver Badge -->
      <v-col
        cols="12"
        md="4"
        class="d-flex justify-center"
      >
        <driver-badge
          :driver-code="driverCode"
          :driver-image-url="driverImageUrl"
          :driver-first-name="driverFirstName"
        />
      </v-col>
    </v-row>
    <!-- Toggle Button -->
    <v-row justify="center">
      <v-btn
        color="primary"
        class="ma-2"
        @click="toggleExpand"
      >
        {{ isExpanded ? 'Hide Order Details' : 'Display Order Details' }}
      </v-btn>
    </v-row>

    <!-- Expanded Order Details -->
    <v-row
      v-if="isExpanded"
      justify="center"
    >
      <delivery-information
        :pieces="pieces"
        :order-key="orderKey"
      />
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { TrackingInfoWithEta, USPackOrderNumber } from "@/types/types";
import { defineComponent, PropType, ref, watch } from "vue";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import OrderLifecycle from "./OrderLifecycle.vue";
import DeliveryInformation, { resetReferenceStates } from './DeliveryInformation.vue';
import DriverBadge from "../common/DriverBadge.vue";

const componentStates: Record<string, boolean> = {};
const currentlyExpandedOrder = ref<string | null>(null);

export function cleanupOrphanedComponentStates(validOrderKeys: Set<string>) {
  Object.keys(componentStates).forEach(orderKey => {
    if (!validOrderKeys.has(orderKey)) {
      delete componentStates[orderKey];
    }
  });

  if (currentlyExpandedOrder.value && !validOrderKeys.has(currentlyExpandedOrder.value)) {
    currentlyExpandedOrder.value = null;
  }
}

const timezonesLong = new Map();
timezonesLong
  .set("EST", "America/New_York")
  .set("CST", "America/Chicago")
  .set("MDT", "America/Salt_Lake_City")
  .set("PST", "America/Los_Angeles")
  .set("MST", "America/Phoenix")
  .set("AKS", "America/Anchorage")
  .set("HST", "America/Honolulu")

dayjs.extend(utc);
dayjs.extend(timezone);

export type PackageDeliveryInfo = {
  Reference: string;
  deliveredItems?: {
    patientIds?: string;
    descriptions?: string;
  }[]
}[]

type Data = {
  step: number;
  lifecycleInfo: {
      PickupDateTime: string,
      DeliveryDateTime: string,
      Eta: string
      AttemptedNotDelivered: boolean
  };
  deliveryWindow: {
    EarlyTime: string,
    LateTime: string,
    DeliveryDateString: string
  }
  shipToAddressInfo: Pick<TrackingInfoWithEta, 'Address' | 'City' | 'State' | 'Zip' | 'StopName'>
  usPackOrderNumber: USPackOrderNumber;
  deliveryHeading: string;
  timezone: boolean;
  driverCode: string;
  driverImageUrl: string;
  driverFirstName: string;
  driverFirstNameUrl: string;
  pieces: PackageDeliveryInfo | undefined;
  isExpanded: boolean;
  orderKey: string;
  expandChange?: () => void;
};

export default defineComponent({
  components: {
    "order-lifecycle": OrderLifecycle,
    "delivery-information": DeliveryInformation,
    "driver-badge": DriverBadge,
  },
  props: {
    order: {
      type: Object as PropType<TrackingInfoWithEta & { AttemptedNotDelivered: boolean } &
      {
        packageDeliveryInfo?: PackageDeliveryInfo
      }>,
      required: true,
    }
  },
  emits: [],
  data(): Data {
    return {
      step: 0,
      lifecycleInfo: {
        PickupDateTime: "",
        DeliveryDateTime: "",
        Eta: "",
        AttemptedNotDelivered: true
      },
      deliveryWindow: {
        EarlyTime: "",
        LateTime: "",
        DeliveryDateString: ""
      },
      shipToAddressInfo: {
        StopName: "",
        Address: "",
        City: "",
        State: "",
        Zip: ""
      },
      usPackOrderNumber: 0,
      deliveryHeading: "Delivery Window",
      timezone: true,
      pieces: [],
      driverCode: "",
      driverImageUrl: "",
      driverFirstNameUrl: "",
      driverFirstName: "",
      isExpanded: false,
      orderKey: "",
      expandChange: undefined
    };
  },
  created() {
    const { EarlyDateTime, LateDateTime, PickupDateTime, DeliveryDateTime, Eta, AttemptedNotDelivered, Timezone, StopName, Address, City, State, Zip, DriverCode, DriverID, packageDeliveryInfo } = this.order;

    this.orderKey = `order-${this.order.OrderID}`;
    this.isExpanded = currentlyExpandedOrder.value === this.orderKey;

    this.expandChange = watch(currentlyExpandedOrder, (newExpandedOrder) => {
      const expectedExpansion = newExpandedOrder === this.orderKey;
      if (this.isExpanded !== expectedExpansion) {
        this.isExpanded = expectedExpansion;
        if (!expectedExpansion) {
          resetReferenceStates(this.orderKey);
        }
      }
    });

    const timezone = Timezone ? timezonesLong.get(Timezone) : 'UTC';
    this.timezone = timezone ? true : false;
    this.step = this.getCurrentStep(PickupDateTime, DeliveryDateTime);
    this.usPackOrderNumber = this.order.OrderID;
    this.driverCode = DriverCode != null ? DriverCode.toString() : "";
    this.driverImageUrl = `${process.env.VUE_APP_TRACKING_BACKEND_URL}/driver/${DriverID}/image`;
    this.driverFirstNameUrl = `${process.env.VUE_APP_TRACKING_BACKEND_URL}/drivers/${DriverID}/driver`;

    fetch(this.driverFirstNameUrl)
    .then(res => {
      if (!res.ok) throw new Error("Failed to fetch");
      return res.text();
    })
    .then(data => {
     this.driverFirstName = data || "Driver";
   })
    .catch(err => {
      console.error("Fetch error:", err);
      this.driverFirstName = "Driver";
    });
    this.pieces = packageDeliveryInfo;

    const EarlyTime = dayjs.utc(EarlyDateTime).format('hh:mm A').toString();
    const LateTime = dayjs.utc(LateDateTime).format('hh:mm A').toString();
    const DeliveryDateString = dayjs.utc(LateDateTime).format('dddd MMM DD, YYYY').toString();
    const PickupDateTimeFormatted = dayjs.utc(PickupDateTime).format('dddd MMM DD, YYYY hh:mm A').toString();
    const DeliveryDateTimeFormatted = dayjs.utc(DeliveryDateTime).format('dddd MMM DD, YYYY hh:mm A').toString();
    const EtaFormatted = dayjs.utc(Eta).tz(timezone).format('dddd MMM DD, YYYY hh:mm A').toString();
    Object.assign(this.deliveryWindow, {
      EarlyTime,
      LateTime,
      DeliveryDateString
    })
    Object.assign(this.lifecycleInfo, {
      PickupDateTime: PickupDateTime ? PickupDateTimeFormatted : null,
      DeliveryDateTime: DeliveryDateTime ? DeliveryDateTimeFormatted : null,
      Eta: Eta ? EtaFormatted : null,
      AttemptedNotDelivered
    });
    Object.assign(this.shipToAddressInfo, {
      StopName,
      Address,
      City,
      State,
      Zip
    });
  },
  methods: {
    getCurrentStep(pickupDateTime: string | null, deliveryDateTime: string | null): number {
     if (deliveryDateTime) return 2;
      else if (pickupDateTime) return 1;
      else return 0;
    },
    onDriverImageError(event: Event) {
      const target = event.target as HTMLImageElement;
      target.src =
        "data:image/svg+xml;base64," +
        btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="gray"><path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z"/></svg>`);
    },
    toggleExpand() {
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      if (this.isExpanded) {
        this.isExpanded = false;
        currentlyExpandedOrder.value = null;
        resetReferenceStates(this.orderKey);
      } else {
        currentlyExpandedOrder.value = this.orderKey;
        this.isExpanded = true;
      }

      componentStates[this.orderKey] = this.isExpanded;
      this.$nextTick(() => {
        this.$nextTick(() => {
          window.scrollTo({ top: scrollPosition, behavior: 'instant' });
          setTimeout(() => {
            window.scrollTo({ top: scrollPosition, behavior: 'instant' });
          }, 50);
        });
      });
    }
  },
});
</script>